import React, { useMemo, useState } from "react";

import {
    WEEKDAYS,
    addMonths,
    buildMonthGrid,
    dayKey,
    dayText,
    dayWithTime,
    firstOfMonth,
    isOverdue,
    monthTitle,
    nextStatus,
    repeatLabel,
    statusLabel,
    timeText,
    toDate,
    userName,
} from "./todoUtils";

/** Сколько задач помещается в клетку дня; остальные — «ещё N». */
const CELL_LIMIT = 3;

/*
 * Календарь: задачи со сроком по дням месяца. Общий — из всех списков; у
 * каждого списка есть и свой (scopeList): только его задачи, с исполнителями —
 * видно, кому и что нужно сделать по дням и времени. Будущие повторы (задачи
 * ещё нет, но она появится) показаны пунктиром. Под сеткой — задачи выбранного
 * дня и быстрая постановка новой сразу со временем.
 */
export default function TodoCalendar({
    month,
    onMonthChange,
    calendar,
    loading = false,
    error = "",
    lists = [],
    /** Календарь одного списка: показываем исполнителей, новые задачи идут в него. */
    scopeList = null,
    meId = "",
    defaultListId = "",
    selectedTaskId = "",
    onOpenTask,
    onStatus,
    onAddTask,
}) {
    const [selectedDay, setSelectedDay] = useState(() => dayKey(new Date()));
    const [title, setTitle] = useState("");
    const [time, setTime] = useState("09:00");
    const [listId, setListId] = useState("");
    const [assigneeId, setAssigneeId] = useState("");
    const [adding, setAdding] = useState(false);
    const [addError, setAddError] = useState("");

    const grid = useMemo(() => buildMonthGrid(month), [month]);
    const todayKey = dayKey(new Date());

    const listTitles = useMemo(() => {
        const map = {};
        [...lists, ...(calendar?.lists || [])].forEach((item) => {
            if (item?._id) map[item._id] = item.title || "";
        });
        return map;
    }, [calendar, lists]);

    /** Задачи и будущие повторы по дням, внутри дня — по времени. */
    const byDay = useMemo(() => {
        const map = {};

        const push = (key, entry) => {
            if (!key) return;
            if (!map[key]) map[key] = [];
            map[key].push(entry);
        };

        (calendar?.tasks || []).forEach((task) => {
            const date = toDate(task.dueAt);
            if (date) push(dayKey(date), { kind: "task", key: task._id, date, task });
        });

        (calendar?.repeats || []).forEach((item, index) => {
            const date = toDate(item.dueAt);
            if (date) push(dayKey(date), { kind: "repeat", key: `r-${item.taskId}-${index}`, date, repeat: item });
        });

        Object.values(map).forEach((items) => items.sort((a, b) => a.date.getTime() - b.date.getTime()));
        return map;
    }, [calendar]);

    const dayItems = byDay[selectedDay] || [];
    const targetListId = scopeList?._id || listId || defaultListId || lists[0]?._id || "";
    const members = Array.isArray(scopeList?.members) ? scopeList.members : [];
    /** Исполнитель новой задачи — только из участников открытого списка. */
    const targetAssigneeId = members.some((user) => String(user?._id || "") === assigneeId) ? assigneeId : "";
    const personName = (user) => userName(user, meId, "");

    const add = async () => {
        const clean = title.trim();
        const dueAt = dayWithTime(new Date(`${selectedDay}T00:00:00`), time);
        if (!clean || !targetListId || !dueAt || adding) return;

        setAdding(true);
        setAddError("");

        try {
            await onAddTask(targetListId, {
                title: clean,
                dueAt,
                ...(targetAssigneeId ? { assigneeId: targetAssigneeId } : {}),
            });
            setTitle("");
        } catch (addTaskError) {
            setAddError(String(addTaskError?.message || "Не удалось добавить задачу"));
        } finally {
            setAdding(false);
        }
    };

    /**
     * Перелистнули месяц — выбранный день переезжает вместе с ним: сегодня,
     * если оно в этом месяце, иначе первое число. Иначе под сеткой остался бы
     * день из другого месяца, задачи которого уже не загружены.
     */
    const goMonth = (next) => {
        const today = new Date();
        const sameMonth = today.getFullYear() === next.getFullYear() && today.getMonth() === next.getMonth();

        onMonthChange(next);
        setSelectedDay(dayKey(sameMonth ? today : next));
    };

    const goToday = () => goMonth(firstOfMonth(new Date()));

    /** Нажали день соседнего месяца (бледный) — открываем тот месяц. */
    const pickDay = (cell) => {
        setSelectedDay(cell.key);
        if (!cell.inMonth) onMonthChange(firstOfMonth(cell.date));
    };

    return (
        <div className="todoCal">
            <div className="todoCal__head">
                <button type="button" onClick={() => goMonth(addMonths(month, -1))} aria-label="Предыдущий месяц">‹</button>
                <h2>{monthTitle(month)}</h2>
                <button type="button" onClick={() => goMonth(addMonths(month, 1))} aria-label="Следующий месяц">›</button>
                <button type="button" className="todoCal__today" onClick={goToday}>Сегодня</button>
                {loading ? <i className="todoSpinner" aria-label="Загружаем" /> : null}
            </div>

            {error ? <div className="todoPage__error" role="alert">{error}</div> : null}

            <div className="todoCal__grid" role="grid">
                {WEEKDAYS.map((name) => (
                    <div key={name} className="todoCal__weekday" role="columnheader">{name}</div>
                ))}

                {grid.map((cell) => {
                    const items = byDay[cell.key] || [];
                    const className = [
                        "todoCal__cell",
                        cell.inMonth ? "" : "is-outside",
                        cell.key === todayKey ? "is-today" : "",
                        cell.key === selectedDay ? "is-selected" : "",
                    ].filter(Boolean).join(" ");

                    return (
                        <button
                            key={cell.key}
                            type="button"
                            className={className}
                            onClick={() => pickDay(cell)}
                            aria-label={`${dayText(cell.date)}, задач: ${items.length}`}
                        >
                            <span className="todoCal__date">{cell.date.getDate()}</span>
                            {items.slice(0, CELL_LIMIT).map((item) => (
                                item.kind === "task" ? (
                                    <span
                                        key={item.key}
                                        className={[
                                            "todoCal__chip",
                                            `is-${item.task.status}`,
                                            item.task.priority === "high" ? "is-high" : "",
                                            isOverdue(item.task) ? "is-overdue" : "",
                                        ].filter(Boolean).join(" ")}
                                    >
                                        <b>{timeText(item.date)}</b> {item.task.title}
                                        {scopeList && item.task.assignee ? <i>{personName(item.task.assignee)}</i> : null}
                                    </span>
                                ) : (
                                    <span key={item.key} className="todoCal__chip is-repeat">
                                        <b>{timeText(item.date)}</b> {item.repeat.title}
                                    </span>
                                )
                            ))}
                            {items.length > CELL_LIMIT ? (
                                <span className="todoCal__more">ещё {items.length - CELL_LIMIT}</span>
                            ) : null}
                        </button>
                    );
                })}
            </div>

            <div className="todoCal__day">
                <h3>
                    {dayText(new Date(`${selectedDay}T00:00:00`))}
                    <small>{dayItems.length ? `задач: ${dayItems.length}` : "задач нет"}</small>
                </h3>

                {dayItems.map((item) => (
                    item.kind === "task" ? (
                        <div
                            key={item.key}
                            className={[
                                "todoRow",
                                `is-${item.task.status}`,
                                selectedTaskId === item.task._id ? "is-selected" : "",
                            ].filter(Boolean).join(" ")}
                        >
                            <button
                                type="button"
                                className={`todoRow__status is-${item.task.status}`}
                                title={`${statusLabel(item.task.status)} → ${statusLabel(nextStatus(item.task.status))}`}
                                onClick={() => onStatus(item.task, nextStatus(item.task.status))}
                                aria-label="Сменить статус"
                            />
                            <button type="button" className="todoRow__main" onClick={() => onOpenTask(item.task)}>
                                <span className="todoRow__title">
                                    {item.task.priority === "high" ? <b className="todoRow__high">!</b> : null}
                                    {item.task.title}
                                </span>
                                <span className={`todoRow__meta ${isOverdue(item.task) ? "is-overdue" : ""}`}>
                                    {timeText(item.date)}
                                    {scopeList
                                        ? (item.task.assignee ? ` · ${personName(item.task.assignee)}` : " · без исполнителя")
                                        : (listTitles[item.task.listId] ? ` · ${listTitles[item.task.listId]}` : "")}
                                    {item.task.repeat?.every && item.task.repeat.every !== "none"
                                        ? ` · ${repeatLabel(item.task.repeat.every).toLowerCase()}`
                                        : ""}
                                </span>
                            </button>
                        </div>
                    ) : (
                        <div key={item.key} className="todoRow is-repeat">
                            <span className="todoRow__status is-repeat" aria-hidden="true" />
                            <span className="todoRow__main">
                                <span className="todoRow__title">{item.repeat.title}</span>
                                <span className="todoRow__meta">
                                    {timeText(item.date)}
                                    {!scopeList && listTitles[item.repeat.listId] ? ` · ${listTitles[item.repeat.listId]}` : ""}
                                    {" · появится по повтору"}
                                </span>
                            </span>
                        </div>
                    )
                ))}

                <form
                    className="todoCal__add"
                    onSubmit={(event) => {
                        event.preventDefault();
                        add();
                    }}
                >
                    <input
                        type="text"
                        value={title}
                        maxLength={300}
                        placeholder="Новая задача на этот день"
                        onChange={(event) => setTitle(event.target.value)}
                    />
                    <input
                        type="time"
                        value={time}
                        onChange={(event) => setTime(event.target.value || "09:00")}
                        aria-label="Время"
                    />
                    {scopeList ? (
                        <select
                            value={targetAssigneeId}
                            onChange={(event) => setAssigneeId(event.target.value)}
                            aria-label="Исполнитель"
                            disabled={!members.length}
                        >
                            <option value="">Без исполнителя</option>
                            {members.map((user) => (
                                <option key={user._id} value={user._id}>{userName(user, meId)}</option>
                            ))}
                        </select>
                    ) : (
                        <select value={targetListId} onChange={(event) => setListId(event.target.value)} aria-label="Список">
                            {lists.map((item) => (
                                <option key={item._id} value={item._id}>{item.title}</option>
                            ))}
                        </select>
                    )}
                    <button type="submit" disabled={!title.trim() || !targetListId || adding}>
                        {adding ? "Добавляем…" : "Добавить"}
                    </button>
                </form>

                {addError ? <div className="todoPage__error" role="alert">{addError}</div> : null}
            </div>
        </div>
    );
}
